import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { Trash2, Plus, Download, Upload } from 'lucide-react';

// GuestListEditor — manages the FULL passenger list for ONE booking, stored in
// the booking_guests table (and nowhere else). Passenger 1 is the primary guest:
// its NAME is locked to the booking's booking_name (it follows the booking) and
// only age/gender are editable here. Positions 2+ are freely editable/removable.
//
// Props:
//   bookingId   (string, required) — the booking whose guests this edits
//   primaryName (string)           — booking_name; the locked Passenger-1 name
//   onSaved     (fn, optional)     — called after a successful save

const GENDERS = ['Male', 'Female', 'Others'];

const emptyGuestRow = () => ({ name: '', age: '', gender: '' });
const isEmptyGuestRow = (r) =>
  !(r.name || '').trim() && !String(r.age ?? '').trim() && !(r.gender || '').trim();

// Creation mode helper: return `prev` adjusted so its length matches the cap's
// additional-row count (cap - 1, since the primary is row 1). Grows by appending
// empty rows; shrinks by dropping ONLY empty rows (trailing first) — a row with
// any data is never removed, so the count may stay above target until saved.
// Returns the SAME array reference when nothing needs to change (avoids churn).
function reconcileAdditional(prev, cap) {
  const target = Math.max(0, cap - 1);
  if (prev.length === target) return prev;
  if (prev.length < target) {
    return [...prev, ...Array.from({ length: target - prev.length }, emptyGuestRow)];
  }
  let removable = prev.length - target;
  const keep = prev.map(() => true);
  for (let i = prev.length - 1; i >= 0 && removable > 0; i--) {
    if (isEmptyGuestRow(prev[i])) { keep[i] = false; removable--; }
  }
  if (keep.every(Boolean)) return prev; // nothing empty to remove
  return prev.filter((_, i) => keep[i]);
}

const lbl = {
  display: 'block', fontSize: '0.68rem', fontWeight: 700,
  color: 'var(--color-ink-muted)', textTransform: 'uppercase',
  letterSpacing: '0.4px', marginBottom: 3,
};
const inp = {
  width: '100%', padding: '7px 9px', border: '1.5px solid var(--color-border-strong)',
  borderRadius: 7, fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box',
  background: '#fff', color: NAVY,
};

// ─── Hand-rolled CSV parser — quoted fields, commas-in-fields, "" escapes, CRLF ──
function parseCSV(text) {
  // Normalize all line endings (CRLF and lone-CR \r) to LF FIRST, so rows split
  // correctly regardless of which OS/tool produced the file. Without this, a
  // CR-only file collapses into a single row and only one guest is imported.
  text = text.replace(/\r\n?/g, '\n');
  const rows = [];
  let field = '', row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

// Validate parsed CSV into { imported: [...], skipped: ['Row N skipped: reason'] }.
// Header row (first cell ~ "name") is detected and skipped; blank lines ignored.
// `cap` is the booking headcount (adults + children); the primary already counts
// as 1, so at most cap-1 additional guests can be imported — valid rows beyond
// that are skipped-and-reported.
function processCSV(text, cap) {
  const raw = parseCSV(text);
  const imported = [], skipped = [];
  const maxAdditional = Math.max(0, cap - 1);
  let start = 0;
  if (raw.length && (raw[0][0] || '').trim().toLowerCase() === 'name') start = 1;

  for (let i = start; i < raw.length; i++) {
    const lineNo = i + 1;                     // 1-based line number as in the file
    const cells = raw[i];
    if (cells.every(c => (c || '').trim() === '')) continue; // blank/trailing line

    const name = (cells[0] || '').trim();
    if (!name) { skipped.push(`Row ${lineNo} skipped: missing name`); continue; }

    let age = '';
    const ageRaw = (cells[1] || '').trim();
    if (ageRaw) {
      if (!/^\d+$/.test(ageRaw) || Number(ageRaw) <= 0) {
        skipped.push(`Row ${lineNo} skipped: invalid age "${ageRaw}"`); continue;
      }
      age = String(Number(ageRaw));
    }

    let gender = '';
    const gRaw = (cells[2] || '').trim();
    if (gRaw) {
      const match = GENDERS.find(g => g.toLowerCase() === gRaw.toLowerCase());
      if (!match) { skipped.push(`Row ${lineNo} skipped: invalid gender "${gRaw}"`); continue; }
      gender = match;
    }

    // Valid row, but enforce the booking headcount cap (primary counts as 1).
    if (imported.length >= maxAdditional) {
      skipped.push(`Row ${lineNo} skipped: exceeds booking headcount of ${cap}`);
      continue;
    }

    imported.push({ name, age, gender });
  }
  return { imported, skipped };
}

// Validate + build the ordered booking_guests rows for a booking from an editor
// snapshot ({ primary:{age,gender}, additional:[{name,age,gender}] }). Returns
// { rows } on success or { error } with a clear message. Shared by both the
// immediate (ViewBookings) save and the deferred (create-time) save so the
// validation, cap guard and position numbering live in ONE place.
export function buildGuestRows({ bookingId, primaryName, primary, additional, cap }) {
  const pAge = String(primary?.age ?? '').trim();
  if (pAge && (!/^\d+$/.test(pAge) || Number(pAge) <= 0)) {
    return { error: 'Passenger 1 age must be a positive whole number.' };
  }

  const keep = [];
  const rowsIn = additional || [];
  for (let i = 0; i < rowsIn.length; i++) {
    const r = rowsIn[i];
    const name = (r.name || '').trim();
    const age = String(r.age ?? '').trim();
    const gender = (r.gender || '').trim();
    if (!name && !age && !gender) continue; // blank row — drop silently
    if (!name) return { error: `Passenger ${i + 2}: name is required.` };
    if (age && (!/^\d+$/.test(age) || Number(age) <= 0)) {
      return { error: `Passenger ${i + 2}: age must be a positive whole number.` };
    }
    if (gender && !GENDERS.includes(gender)) {
      return { error: `Passenger ${i + 2}: gender must be Male, Female or Others.` };
    }
    keep.push({ name, age: age ? Number(age) : null, gender: gender || null });
  }

  if (1 + keep.length > cap) {
    return { error: `Guest list can't exceed the booking headcount of ${cap} (adults + children).` };
  }

  const rows = [
    {
      booking_id: bookingId, position: 1, name: primaryName || '',
      age: pAge ? Number(pAge) : null, gender: primary?.gender || null,
    },
    ...keep.map((r, idx) => ({
      booking_id: bookingId, position: idx + 2, name: r.name, age: r.age, gender: r.gender,
    })),
  ];
  return { rows };
}

// True when the editor snapshot holds anything worth saving (any additional
// guest, or an age/gender set on the primary). Lets a creation form skip writing
// a lone primary row when staff never touched the optional guest list.
export function hasGuestData(snapshot) {
  if (!snapshot) return false;
  const add = snapshot.additional || [];
  const hasAdditional = add.some(r => !isEmptyGuestRow(r));
  return hasAdditional || !!snapshot.primary?.age || !!snapshot.primary?.gender;
}

// Persist a snapshot to booking_guests for a KNOWN booking id: clean replace
// (delete this booking's rows, then insert the built set). Returns { error } on
// any validation/DB failure, else { rows }. Used by immediate and deferred saves.
export async function persistGuestList(bookingId, snapshot) {
  const built = buildGuestRows({ ...snapshot, bookingId });
  if (built.error) return { error: built.error };
  const del = await supabase.from('booking_guests').delete().eq('booking_id', bookingId);
  if (del.error) return { error: del.error.message };
  const ins = await supabase.from('booking_guests').insert(built.rows);
  if (ins.error) return { error: ins.error.message };
  return { rows: built.rows };
}

export default function GuestListEditor({ bookingId, primaryName, adults, children, onSaved, deferred = false, onChange }) {
  // Headcount cap = adults + children (infants and tour leaders are NOT counted,
  // same rule as the food count). The primary always exists and counts as 1, so
  // the cap is at least 1 even if the booking has no adults/children recorded.
  const cap = Math.max(1, (Number(adults) || 0) + (Number(children) || 0));
  const [primary, setPrimary]       = useState({ age: '', gender: '' });
  const [additional, setAdditional] = useState([]); // [{ name, age, gender }]
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [status, setStatus]         = useState('');  // success toast text
  const [pendingImport, setPendingImport] = useState(null); // { imported, skipped }
  const [importSummary, setImportSummary] = useState(null); // { count, skipped }
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    // Deferred (create-time) mode: there's no booking id yet, so nothing to load.
    // The list is held in memory and reported to the parent via onChange.
    if (deferred || !bookingId) { setLoading(false); return; }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('booking_guests')
      .select('position, name, age, gender')
      .eq('booking_id', bookingId)
      .order('position', { ascending: true });
    if (err) {
      setError(err.message);
    } else {
      setError('');
      const rows = data || [];
      const p = rows.find(r => r.position === 1);
      setPrimary({ age: p?.age != null ? String(p.age) : '', gender: p?.gender || '' });
      setAdditional(
        rows.filter(r => r.position >= 2).map(r => ({
          name: r.name || '',
          age: r.age != null ? String(r.age) : '',
          gender: r.gender || '',
        }))
      );
    }
    setLoading(false);
  }, [bookingId, deferred]);

  useEffect(() => { load(); }, [load]);

  // Deferred mode: report the in-memory list to the parent on every change so it
  // can persist after the booking id exists. A ref keeps onChange identity-stable
  // so this never loops on the parent re-rendering.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });
  useEffect(() => {
    if (deferred && onChangeRef.current) onChangeRef.current({ primary, additional, cap });
  }, [deferred, primary, additional, cap]);

  // Creation mode only: pre-populate enough empty additional rows to reach the
  // cap so staff can type straight in without clicking "Add passenger". Runs on
  // mount and whenever the cap changes (live pax edits); grows by adding empties
  // and shrinks by dropping empties only — never deletes a row with data.
  useEffect(() => {
    if (!deferred) return;
    setAdditional(prev => reconcileAdditional(prev, cap));
  }, [deferred, cap]);

  const busy = saving;

  const clearTransient = () => { setStatus(''); setError(''); };

  const updateAdditional = (i, field, val) => {
    clearTransient();
    setAdditional(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  };
  const addRow = () => { clearTransient(); setAdditional(prev => [...prev, { name: '', age: '', gender: '' }]); };
  const removeRow = (i) => { clearTransient(); setAdditional(prev => prev.filter((_, idx) => idx !== i)); };

  // ── CSV: download template ────────────────────────────────────────────────
  const downloadTemplate = () => {
    const csv = 'Name,Age,Gender\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'guest-list-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // ── CSV: pick + parse a file, then confirm before replacing ───────────────
  const handleFile = async (file) => {
    if (!file) return;
    clearTransient();
    setImportSummary(null);
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please choose a .csv file.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    const text = await file.text();
    const result = processCSV(text, cap);
    if (fileRef.current) fileRef.current.value = '';
    if (!result.imported.length && !result.skipped.length) {
      setError('No guest rows found in that file.');
      return;
    }
    setPendingImport(result); // show the replace confirmation
  };

  const confirmImport = () => {
    setAdditional(pendingImport.imported);
    setImportSummary({ count: pendingImport.imported.length, skipped: pendingImport.skipped });
    setPendingImport(null);
  };

  // ── Save (immediate mode): replace this booking's guest rows with the editor
  // set, via the shared persist helper (same validation/cap/build as create-time).
  const handleSave = async () => {
    clearTransient();
    // Validate up front so row/cap messages show raw (unchanged from before);
    // only DB failures get the "Could not save:" prefix.
    const built = buildGuestRows({ bookingId, primaryName, primary, additional, cap });
    if (built.error) { setError(built.error); return; }
    setSaving(true);
    const res = await persistGuestList(bookingId, { primaryName, primary, additional, cap });
    setSaving(false);
    if (res.error) { setError(`Could not save: ${res.error}`); return; }

    // Reflect the saved, renumbered set in the editor (rows[0] is the primary).
    const saved = res.rows;
    setAdditional(saved.filter(r => r.position >= 2).map(r => ({
      name: r.name, age: r.age != null ? String(r.age) : '', gender: r.gender || '',
    })));
    setImportSummary(null);
    setStatus(`Guest list saved (${saved.length} passenger${saved.length === 1 ? '' : 's'}).`);
    if (onSaved) onSaved();
  };

  if (loading) {
    return <p style={{ color: 'var(--color-ink-faint)', fontSize: '0.82rem', margin: 0 }}>Loading guest list…</p>;
  }

  // Columns + gap live in App.css (.guest-grid-row) so the phone breakpoint
  // can retune them; only the alignment stays inline.
  const rowGrid = { display: 'grid', alignItems: 'end' };

  const total = 1 + additional.length;      // primary + additional
  const atCap = total >= cap;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '0 0 12px' }}>
        <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', margin: 0 }}>
          Optional full passenger list. Passenger 1 is the primary guest (name follows the booking);
          add the rest below or import a CSV.
        </p>
        <span style={{ flexShrink: 0, fontSize: '0.74rem', fontWeight: 700, color: atCap ? 'var(--color-accent-strong)' : 'var(--color-ink-secondary)', background: 'var(--color-sunken)', border: '1px solid var(--color-border)', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
          Guests: {total} of {cap}
        </span>
      </div>

      {/* Column headers */}
      <div className="guest-grid-row" style={{ ...rowGrid, marginBottom: 6 }}>
        <span />
        <span style={lbl}>Name</span>
        <span style={lbl}>Age</span>
        <span style={lbl}>Gender</span>
        <span />
      </div>

      {/* Passenger 1 — locked name, editable age/gender */}
      <div className="guest-grid-row" style={{ ...rowGrid, marginBottom: 8 }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-ink-muted)', paddingBottom: 8 }}>1</span>
        <input
          style={{ ...inp, background: 'var(--color-sunken)', color: 'var(--color-ink-secondary)', cursor: 'not-allowed' }}
          value={primaryName || ''}
          readOnly
          title="Primary guest name follows the booking and can't be edited here"
        />
        <input
          style={inp} type="number" min="1" placeholder="—"
          value={primary.age}
          onChange={(e) => { clearTransient(); setPrimary(p => ({ ...p, age: e.target.value })); }}
          disabled={busy}
        />
        <select
          style={inp}
          value={primary.gender}
          onChange={(e) => { clearTransient(); setPrimary(p => ({ ...p, gender: e.target.value })); }}
          disabled={busy}
        >
          <option value="">—</option>
          {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <span />
      </div>

      {/* Additional passengers */}
      {additional.map((r, i) => (
        <div key={i} className="guest-grid-row" style={{ ...rowGrid, marginBottom: 8 }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-ink-muted)', paddingBottom: 8 }}>{i + 2}</span>
          <input
            style={inp} placeholder="Guest name"
            value={r.name}
            onChange={(e) => updateAdditional(i, 'name', e.target.value)}
            disabled={busy}
          />
          <input
            style={inp} type="number" min="1" placeholder="—"
            value={r.age}
            onChange={(e) => updateAdditional(i, 'age', e.target.value)}
            disabled={busy}
          />
          <select
            style={inp}
            value={r.gender}
            onChange={(e) => updateAdditional(i, 'gender', e.target.value)}
            disabled={busy}
          >
            <option value="">—</option>
            {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button
            type="button" className="btn btn-ghost btn-sm"
            style={{ padding: '6px', color: 'var(--color-danger-strong)' }}
            onClick={() => removeRow(i)} disabled={busy}
            title="Remove passenger" aria-label="Remove passenger"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={addRow} disabled={busy || atCap}>
          <Plus size={14} style={{ marginRight: 5 }} />Add passenger
        </button>
        {atCap && (
          <span style={{ fontSize: '0.74rem', fontWeight: 600, color: 'var(--color-ink-muted)' }}>
            Headcount reached ({total} of {cap})
          </span>
        )}
      </div>

      {/* CSV controls */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={downloadTemplate} disabled={busy}>
            <Download size={14} style={{ marginRight: 5 }} />Download template
          </button>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files?.[0])} />
          <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Upload size={14} style={{ marginRight: 5 }} />Import CSV
          </button>
        </div>
        <p style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)', margin: '8px 0 0' }}>
          CSV columns: Name, Age, Gender. The primary guest is Passenger 1 — do NOT include them; the file is
          for additional guests only. Import replaces the current additional guests.
        </p>
      </div>

      {/* Import replace confirmation */}
      {pendingImport && (
        <div style={{ marginTop: 12, padding: '12px 14px', border: '1px solid var(--color-border-strong)', borderRadius: 8, background: 'var(--color-sunken)' }}>
          <p style={{ fontSize: '0.82rem', fontWeight: 600, color: NAVY, margin: '0 0 4px' }}>
            This will replace the current additional guests.
          </p>
          <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', margin: '0 0 10px' }}>
            {pendingImport.imported.length} valid row{pendingImport.imported.length === 1 ? '' : 's'} found
            {pendingImport.skipped.length ? `, ${pendingImport.skipped.length} skipped` : ''}. Replace the
            current {additional.length} additional guest{additional.length === 1 ? '' : 's'}?
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-navy btn-sm" onClick={confirmImport}>Replace</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPendingImport(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Import summary */}
      {importSummary && (
        <div style={{ marginTop: 12, padding: '10px 14px', border: '1px solid var(--color-border)', borderRadius: 8 }}>
          <p style={{ fontSize: '0.82rem', fontWeight: 700, color: '#15803d', margin: 0 }}>
            {importSummary.count} guest{importSummary.count === 1 ? '' : 's'} imported. Review and Save to persist.
          </p>
          {importSummary.skipped.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {importSummary.skipped.map((s, i) => (
                <li key={i} style={{ fontSize: '0.76rem', color: 'var(--color-danger-strong)', marginBottom: 2 }}>{s}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <div className="toast toast-error" style={{ marginTop: 12 }}>{error}</div>}
      {!deferred && status && <div className="toast toast-success" style={{ marginTop: 12 }}>{status}</div>}

      {/* Deferred (create-time): no own Save button — the list is saved together
          with the booking once its id exists. */}
      {deferred ? (
        <p style={{ marginTop: 14, fontSize: '0.74rem', color: 'var(--color-ink-faint)' }}>
          This guest list is saved automatically when the booking is created.
        </p>
      ) : (
        <div style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-navy" onClick={handleSave} disabled={busy}>
            {saving ? 'Saving…' : 'Save guest list'}
          </button>
        </div>
      )}
    </div>
  );
}
